import React from 'react';
import { BobbingEmoji, FunButton, FunCard, GameHero, Pill, Toast } from './GameUI';
import PictureSetPicker, { pictureSetLabel } from './PictureSetPicker';

const COPIED_MS = 2000;

function displayName(user) {
  if (!user) return null;
  return user.nickname || user.first_name || 'Your partner';
}

const WaitingDots = () => (
  <span className="gpDots" aria-hidden="true">
    <span className="gpDots__dot" />
    <span className="gpDots__dot" />
    <span className="gpDots__dot" />
  </span>
);

const CodeTiles = ({ code }) => (
  <div className="gpCodeTiles" aria-label={`Game code ${code}`}>
    {String(code).split('').map((char, i) => (
      <span
        key={`${char}-${i}`}
        className="gpCodeTiles__tile"
        style={{ transform: `rotate(${i % 2 === 0 ? -4 : 4}deg)` }}
        aria-hidden="true"
      >
        {char}
      </span>
    ))}
  </div>
);

const StatusCard = ({ emoji, title, subtitle, waiting, entranceIndex }) => (
  <FunCard entranceIndex={entranceIndex} className="gpStatus">
    <BobbingEmoji emoji={emoji} size={56} tilt="6deg" className="gpStatus__emoji" />
    <div className="gpStatus__text">
      <p className="gpStatus__title">{title}</p>
      {!!subtitle && <p className="gpStatus__subtitle">{subtitle}</p>}
    </div>
    {waiting && <WaitingDots />}
  </FunCard>
);

// Who in the lobby is new to this game type, as a sentence for the practice prompt.
function newcomerLine(session, guestName) {
  const played = session.played_before;
  if (!played) return null;
  const hostNew = played.host === false;
  const guestNew = played.guest === false;
  if (hostNew && guestNew) return "Neither of you has played this game before.";
  if (hostNew) return "You haven't played this game before.";
  if (guestNew) return `${guestName} hasn't played this game before.`;
  return null;
}

class GameLobby extends React.Component {
  state = { copied: false, startingWithPractice: null };

  start = (practice) => {
    this.setState({ startingWithPractice: practice });
    this.props.onStart({ practice });
  };

  renderStart(guestName) {
    const { session, busy } = this.props;
    const loading = (practice) => busy && this.state.startingWithPractice === practice;

    if (!guestName) {
      return <FunButton variant="primary" emoji="🚀" text="Start game" disabled />;
    }

    const hasPractice = !session.game || session.game.has_practice !== false;
    if (!hasPractice) {
      return (
        <FunButton variant="success" emoji="🚀" text="Start game" loading={loading(false)} onClick={() => this.start(false)} />
      );
    }

    const newcomers = newcomerLine(session, guestName);
    if (newcomers) {
      return (
        <FunCard entranceIndex={2} className="gpPracticeAsk">
          <div className="gpPracticeAsk__row">
            <BobbingEmoji emoji="🧪" size={48} tilt="-6deg" />
            <p className="gpPracticeAsk__text">
              {newcomers} Want to do a couple of practice rounds before the game starts?
            </p>
          </div>
          <FunButton
            variant="success"
            emoji="🧪"
            text="Yes, practice first"
            disabled={busy}
            loading={loading(true)}
            onClick={() => this.start(true)}
          />
          <FunButton
            variant="soft"
            emoji="🚀"
            text="No, start the game"
            disabled={busy}
            loading={loading(false)}
            onClick={() => this.start(false)}
            className="gpSpaceTop"
          />
        </FunCard>
      );
    }

    return (
      <div>
        <FunButton
          variant="success"
          emoji="🚀"
          text="Start game"
          disabled={busy}
          loading={loading(false)}
          onClick={() => this.start(false)}
        />
        <FunButton
          variant="soft"
          emoji="🧪"
          text="Start with 2 practice rounds"
          disabled={busy}
          loading={loading(true)}
          onClick={() => this.start(true)}
          className="gpSpaceTop"
        />
      </div>
    );
  }

  componentWillUnmount() {
    clearTimeout(this.copiedTimer);
  }

  invite() {
    const { session } = this.props;
    const title = session.activity ? session.activity.title : 'a Lexody game';
    return {
      url: `${window.location.origin}/games?code=${session.code}`,
      text: `Play ${title} with me on Lexody! Open Games → Join a game and enter code ${session.code}`,
    };
  }

  copyLink = async () => {
    const { url, text } = this.invite();
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      this.setState({ copied: true });
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => this.setState({ copied: false }), COPIED_MS);
    } catch (error) {
      window.prompt('Copy this invite link', url);
    }
  };

  shareCode = () => {
    const { url, text } = this.invite();
    navigator.share({ title: 'Lexody game', text, url }).catch(() => null);
  };

  renderHost() {
    const { session } = this.props;
    const guestName = displayName(session.guest);
    const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
    return (
      <div className="gpSplit gpSplit--lobby">
        {this.renderHeader(true)}
        <div className="gpSplit__hero">
          <GameHero emoji="🎟️" kicker="YOUR GAME CODE" title="Share it with your partner">
            <CodeTiles code={session.code} />
            <div className="gpLobby__shareRow">
              <FunButton
                variant="light"
                size="small"
                emoji={this.state.copied ? '✅' : '🔗'}
                text={this.state.copied ? 'Link copied!' : 'Copy invite link'}
                onClick={this.copyLink}
              />
              {canShare && (
                <FunButton variant="light" size="small" emoji="📤" text="Share code" onClick={this.shareCode} />
              )}
            </div>
          </GameHero>
        </div>

        <div className="gpSplit__main">
          {guestName
            ? <StatusCard emoji="🎉" title={`${guestName} joined!`} subtitle="You're all set. Let the games begin!" entranceIndex={1} />
            : <StatusCard emoji="👀" title="Waiting for your partner" subtitle="They'll pop in here once they enter the code." waiting entranceIndex={1} />}

          <div className="gpSpaceTop">
            <PictureSetPicker
              source={session.game}
              selected={session.game && session.game.picture_set}
              extendedUnlocked={session.game && session.game.extended_unlocked}
              locked={!this.props.premiumUser}
              disabled={this.props.busy}
              onChange={this.props.onPictureSet}
              onUpgrade={this.props.onUpgrade}
            />
          </div>

          {this.renderStart(guestName)}
          {this.renderLeave(true)}
        </div>
      </div>
    );
  }

  renderHeader(isHost) {
    const { session } = this.props;
    const language = session.language ? session.language.language : null;
    if (!session.activity && !language) return null;
    return (
      <header className="gpLobby__header gpSplit__top">
        {session.activity && <h1 className="gpLobby__title">{session.activity.title}</h1>}
        {language && <Pill emoji="🗣️" text={`Speak only in ${language}`} />}
        {!isHost && pictureSetLabel(session.game) && (
          <Pill emoji="🖼️" text={pictureSetLabel(session.game)} />
        )}
      </header>
    );
  }

  renderLeave(isHost) {
    return (
      <FunButton
        variant="ghost"
        emoji={isHost ? '✖️' : '👋'}
        text={isHost ? 'Cancel game' : 'Leave'}
        onClick={this.props.onLeave}
        className="gpSpaceTopLarge"
      />
    );
  }

  renderGuest() {
    const hostName = displayName(this.props.session.host) || 'the host';
    return (
      <StatusCard
        emoji="🙌"
        title="You're in!"
        subtitle={`Waiting for ${hostName} to start the game.`}
        waiting
        entranceIndex={1}
      />
    );
  }

  render() {
    const { session } = this.props;
    const isHost = session.host_user_id === this.props.myUserId;
    return (
      <div>
        {isHost ? this.renderHost() : (
          <div>
            {this.renderHeader(false)}
            {this.renderGuest()}
            {this.renderLeave(false)}
          </div>
        )}
        {this.props.error !== '' && <Toast key={this.props.error} emoji="😬" text={this.props.error} />}
      </div>
    );
  }
}

export default GameLobby;
